import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/ui/icon';

export interface Crumb {
  label: string;
  /** Supplied when the crumb is somewhere the reader can actually go back to. */
  onClick?: () => void;
}

/**
 * The trail the detail and wizard frames draw above the title.
 *
 * Several of these screens are panes rather than routes, so there is no browser
 * Back to press and no href to give a crumb: the order and recipe panels need
 * the first crumb to *be* the way out, while the menu wizard's trail is only a
 * statement of position. A crumb with an `onClick` is therefore a button and one
 * without is text — the affordance follows what the crumb can do, instead of
 * every screen deciding again.
 *
 * `label` names the landmark, because a page can hold two navs and "navigation,
 * navigation" tells a reader nothing. The chevron mirrors itself in Arabic
 * through `<Icon>`; nothing here positions by `left`/`right`.
 */
export function Breadcrumb({
  label,
  trail,
  current,
  className,
}: {
  label: string;
  trail: readonly Crumb[];
  current: string;
  className?: string;
}) {
  return (
    <nav aria-label={label} className={cn('flex flex-wrap items-center gap-1 text-sm', className)}>
      {trail.map((crumb) => (
        <span key={crumb.label} className="flex items-center gap-1 text-ink-muted">
          {crumb.onClick ? (
            <button
              type="button"
              onClick={crumb.onClick}
              className="rounded hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {crumb.label}
            </button>
          ) : (
            crumb.label
          )}
          <Icon as={ChevronRight} size="sm" aria-hidden />
        </span>
      ))}
      <span aria-current="page" className="font-medium">
        {current}
      </span>
    </nav>
  );
}
